import type { EnergyProvider, ResolvedProvider } from "../energyProvider";

/**
 * Regions an entry is verified for, i.e. the keys of its per-region pricing.
 * Null means the pricing does not vary by region (all NL entries).
 */
export const providerRegions = (provider: EnergyProvider): string[] | null =>
  provider.pricingByRegion ? Object.keys(provider.pricingByRegion) : null;

/**
 * Resolve an entry for one region: that region's pricing block overrides the entry-level
 * defaults. Returns null when the entry has no verified pricing for the region, which is
 * what keeps the strict filter strict — callers must treat null as "not available here".
 *
 * The casts are safe because `FlatProvider` and the per-region invariant (enforced in
 * `countries.test.ts`) guarantee the pricing fields are present after merging.
 */
export const resolveProvider = (
  provider: EnergyProvider,
  regionId: string | null
): ResolvedProvider | null => {
  const regions = providerRegions(provider);
  if (!regions) {
    return { ...provider, regions: null } as ResolvedProvider;
  }
  const pricing = regionId === null ? undefined : provider.pricingByRegion?.[regionId];
  if (!pricing) {
    return null;
  }
  const base = { ...provider };
  delete base.pricingByRegion;
  return { ...base, ...pricing, regions } as ResolvedProvider;
};

/**
 * Providers available in a region, each resolved to that region's pricing.
 * `regionId` is null for countries without regions, where every entry passes through.
 */
export const providersForRegion = (
  providers: EnergyProvider[],
  regionId: string | null
): ResolvedProvider[] =>
  providers
    .map((provider) => resolveProvider(provider, regionId))
    .filter((provider): provider is ResolvedProvider => provider !== null);

export const cheapestProviderId = (
  providers: ResolvedProvider[]
): string | null =>
  providers.length === 0
    ? null
    : providers.reduce((cheapest, provider) =>
        provider.perKwhChargeExcludingVat < cheapest.perKwhChargeExcludingVat
          ? provider
          : cheapest
      ).id;

export const resolveSelectedProviderId = (
  providers: ResolvedProvider[],
  selectedId: string
): string =>
  providers.some((provider) => provider.id === selectedId)
    ? selectedId
    : cheapestProviderId(providers) ?? "";
